import {
    buildInitialFilters,
    composeColumnRenderer,
    FormInputCell,
    GridColumnType,
    renderColumnFilter
} from "@repo/design-system/components/grids";
import { Iconify, NoRecordFound } from "@repo/design-system/components/ui";
import { FilterMatchMode } from "primereact/api";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Skeleton } from 'primereact/skeleton';
import PropTypes from "prop-types";
import React, { useCallback, useMemo, useState } from "react";
import "./GridBuilder.scss";
import { DefaultActions } from "./config/ActionConfig";

const GridBuilderComponent = ({
    dataSource = [],
    config = [],
    paginator = true,
    rows = 10,
    rowsPerPageOptions = [5, 10, 25, 50, 100, 200],
    sortMode = "multiple",
    tableStyle = { minWidth: "50rem" },
    actionHandlers = {},
    columnEvents = {},
    scrollHeight,
    loading = false,
    noRecordFoundMessage = "No Record Found",
    ...rest
}) => {

    const { columns, filtersConfig } = config;

    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        ...buildInitialFilters(columns)
    });

    const skeletonData = useMemo(() => {
        return loading ? Array.from({ length: rows }, (_, i) => ({ _skeletonId: i })) : null;
    }, [loading, rows]);

    const onGlobalSearch = (dataField, value) => {
        setFilters(prev => ({
            ...prev,
            global: {
                ...prev.global,
                value: value
            }
        }));
    };

    const renderActions = useCallback((actions) => (rowData) => {
        if (!actions || actions.length === 0) return null;

        return (
            <div className="grid-actions">
                {actions.map((action, index) => {
                    const handlerFn = actionHandlers[action.handler];
                    return (
                        <Iconify
                            key={index}
                            icon={action.icon}
                            width={action.iconWidth}
                            ttLabel={action?.ttLabel ?? ""}
                            targetId={action?.targetId ?? ""}
                            className={`action-icon ${action.type}`}
                            onClick={handlerFn ? () => handlerFn(rowData) : undefined}
                        />
                    );
                })}
            </div>
        );
    }, [actionHandlers]);

    const resolvedColumns = useMemo(() => {
        return columns?.map((col) => {
            if (col.type !== GridColumnType.ACTION) return col;

            let actions = [];

            if (col.defaultActions) {
                actions = DefaultActions.filter(
                    a => col.defaultActions[a.key]
                );
            }

            if (Array.isArray(col.customActions)) {
                actions = [...actions, ...col.customActions];
            }

            if (!actions.length) return { ...col, _resolvedActions: null };

            return {
                ...col,
                _resolvedActions: actions,
                _actionRenderer: renderActions(actions)
            };
        });
    }, [columns, renderActions]);

    const getColumnBody = useCallback(
        (col) => composeColumnRenderer(col, columnEvents),
        [columnEvents]
    );

    const getSkeletonBody = useCallback((col) => {
        if (!loading) return getColumnBody(col);

        // Action columns get narrower skeleton circles
        if (col.type === GridColumnType.ACTION) {
            return () => (
                <div style={{ display: "flex", gap: 8 }}>
                    <Skeleton shape="circle" size="1.8rem" />
                    <Skeleton shape="circle" size="1.8rem" />
                </div>
            );
        }

        // Regular columns get shimmer bars with varied widths for realism
        return (_, { rowIndex }) => (
            <Skeleton
                height="0.9rem"
                width={`${50 + ((rowIndex * 17 + col.field?.length * 7) % 35)}%`}
                borderRadius="6px"
            />
        );
    }, [loading, getColumnBody]);

    const globalFilterFields = useMemo(() => {
        return columns?.filter(col => col.field && col.type !== GridColumnType.ACTION)
            .map(col => col.field);
    }, [columns]);

    return (
        <div className="grid-wrapper">
            {filtersConfig?.enabled && (
                <div className="row mb-3">
                    <FormInputCell id="search" name="search" cssClass=""
                        value={filters.global.value || ""} onBlur={() => ""}
                        onChange={(dataField, value) => onGlobalSearch(dataField, value)}
                        {...filtersConfig}
                    />
                </div>
            )}

            <DataTable
                value={loading ? skeletonData : dataSource}
                loading={false}
                emptyMessage={<NoRecordFound message={noRecordFoundMessage} />}
                // paginator={dataSource.length > 0 && paginator}
                paginator={paginator}
                rows={rows}
                rowsPerPageOptions={rowsPerPageOptions}
                sortMode={sortMode}
                tableStyle={tableStyle}
                scrollable={true}
                filters={filters}
                filterDisplay={filtersConfig?.filterDisplay || ""} // 'row' | 'menu'
                globalFilterFields={filtersConfig?.filterFields || globalFilterFields}
                scrollHeight={scrollHeight || "50vh"}
                {...rest}
            >
                {resolvedColumns?.map((col, index) => {
                    if (col.field === "actions" && !col._resolvedActions) return null;

                    return (
                        <Column
                            key={col.field || index}
                            field={col.field}
                            header={col.header}
                            sortable={!loading && col?.config?.sortable}
                            body={getSkeletonBody(col)}
                            style={{ width: col.width }}
                            className={col.className}
                            filter={!loading && !!col?.config?.filter}
                            filterField={col.field}
                            filterElement={renderColumnFilter(col)}
                        />
                    );
                })}
            </DataTable>
        </div>
    );
};

GridBuilderComponent.prototype = {
    dataSource: PropTypes.array,
    config: PropTypes.shape({
        columns: PropTypes.array
    }),
    paginator: PropTypes.bool,
    rows: PropTypes.number,
    rowsPerPageOptions: PropTypes.array,
    sortMode: PropTypes.string,
    tableStyle: PropTypes.object,
    actionHandlers: PropTypes.object,
    columnEvents: PropTypes.object,
    enableSearch: PropTypes.bool,
    searchPlaceholder: PropTypes.string,
    scrollHeight: PropTypes.string,
};

export const GridBuilder = React.memo(GridBuilderComponent);
export const GridBuilderSkeleton = React.memo(GridBuilderComponent);